/*
 OK FBI — TikTok Event API Bridge
 Opens ONE persistent WebSocket from the Host browser to the local
 TikTok Event API service running on the same streaming PC.

 Design:
   • Runs invisibly in the app root (mounted globally, once).
   • Never renders on /overlay — broadcast surface must stay clean.
   • On open  → POST /api/tiktok/bridge-state {state:"open"}   (armed)
   • On chat  → POST /api/tiktok/event-ingest {event:"chat",...}
                (backend flips real_live=true & handles voting)
   • On error/close → POST /api/tiktok/bridge-state {state:"error"|"closed"}
                and auto-reconnect with exponential back-off.
   • Detects HTTPS + ws:// mixed-content block and shows a truthful
     non-branded banner instructing the host to allow local-network access.

 No third-party branding is ever printed. Internal name only.
*/
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, X } from "lucide-react";

const EVENT_API_URL = "ws://localhost:21213/";
const RECONNECT_BASE_MS = 1500;
const RECONNECT_MAX_MS  = 30000;

export function TikTokEventBridge() {
  const location = useLocation();
  const isOverlay = location?.pathname === "/overlay";
  const isLogin = location?.pathname === "/login";

  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  // banner: null | { kind: 'mixed' | 'unreachable' | 'closed' }
  const wsRef = useRef(null);
  const backoffRef = useRef(RECONNECT_BASE_MS);
  const stoppedRef = useRef(false);
  const firstOpenRef = useRef(false);

  // Detect mixed-content risk BEFORE trying to connect. If the site is HTTPS
  // and no override is set, the browser will silently block ws://. We still
  // attempt but surface a clear message.
  const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";

  useEffect(() => {
    if (isOverlay || isLogin) return;   // do not run on overlay/login pages
    stoppedRef.current = false;

    const connect = () => {
      if (stoppedRef.current) return;
      let ws;
      try { ws = new WebSocket(EVENT_API_URL); }
      catch (e) {
        // Synchronous throw (e.g., mixed-content SecurityError in strict browsers)
        setBanner({ kind: isHttps ? "mixed" : "unreachable" });
        api.post("/tiktok/bridge-state", { state: "error", reason: "constructor_error" }).catch(() => {});
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        firstOpenRef.current = true;
        setBanner(null);
        backoffRef.current = RECONNECT_BASE_MS;
        api.post("/tiktok/bridge-state", { state: "open" }).catch(() => {});
      };

      ws.onmessage = (ev) => {
        let msg = null;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (!msg || !msg.event) return;
        const kind = String(msg.event).toLowerCase();
        // Accept common event names; ignore the rest.
        if (!["chat", "member", "gift", "like"].includes(kind)) return;
        const d = msg.data || {};
        // Multiple TikTok libraries expose slightly different field names;
        // support the most common ones without ever guessing.
        const user_id =
          d.userId || d.uniqueId || d.userSecId || d.userIdStr ||
          d.user_id || d.user?.userId || d.user?.uniqueId ||
          (d.uid != null ? String(d.uid) : null);
        const username = d.uniqueId || d.username || d.user?.uniqueId || d.userInfo?.uniqueId || null;
        const display_name = d.nickname || d.displayName || d.user?.nickname || d.userInfo?.nickname || username;
        const avatar =
          d.profilePictureUrl || d.avatar || d.userInfo?.profilePicture?.urls?.[0] ||
          d.user?.profilePicture?.urls?.[0] || null;
        const comment = d.comment || d.text || d.message || (kind === "gift" ? d.giftName : null) || "";

        if (kind === "chat" && !comment) return;   // skip empty chat noise
        api.post("/tiktok/event-ingest", {
          event: kind,
          user_id: user_id ? String(user_id) : (username || null),
          username, display_name, avatar,
          comment,
        }).catch(() => { /* silent — worst case we drop one event */ });
      };

      ws.onclose = (ev) => {
        wsRef.current = null;
        // If we NEVER managed to open, the port is closed or blocked.
        if (!firstOpenRef.current) setBanner({ kind: isHttps ? "mixed" : "unreachable" });
        else setBanner({ kind: "closed" });
        api.post("/tiktok/bridge-state", { state: "closed", reason: `code_${ev?.code || ""}` }).catch(() => {});
        scheduleReconnect();
      };

      ws.onerror = () => {
        api.post("/tiktok/bridge-state", { state: "error", reason: "socket_error" }).catch(() => {});
        // onclose will follow — no need to schedule here.
      };
    };

    const scheduleReconnect = () => {
      if (stoppedRef.current) return;
      const wait = backoffRef.current;
      backoffRef.current = Math.min(RECONNECT_MAX_MS, Math.floor(wait * 1.6));
      setTimeout(connect, wait);
    };

    connect();

    return () => {
      stoppedRef.current = true;
      try { wsRef.current && wsRef.current.close(); } catch {}
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverlay]);

  if (isOverlay || isLogin || !banner || dismissed) return null;

  // Only surface this banner on STREAMER/OWNER surfaces where TikTok setup is
  // actually relevant. On the public homepage, /app investigator surfaces,
  // /plans etc it is intrusive and confuses normal users.
  const path = location?.pathname || "";
  const isStreamerOrOwnerArea = path.startsWith("/host/") || path.startsWith("/owner/") || path === "/host";
  if (!isStreamerOrOwnerArea) return null;

  // Truthful, non-branded banner. Shown only when the local Event API cannot
  // be reached from the browser (usually mixed-content or the desktop app
  // isn't running yet). Positioned so it doesn't cover primary UI.
  const msg = banner.kind === "mixed"
    ? "ملاحظة: البث المباشر يعمل عبر الـ Webhook السحابي بدون الحاجة للشبكة المحلية."
    : banner.kind === "unreachable"
    ? "تعذر الاتصال بخدمة TikTok المحلية · يعمل النظام بالـ Webhook السحابي تلقائياً."
    : "انقطع اتصال TikTok · جاري إعادة المحاولة تلقائياً.";

  return createPortal(
    <div
      dir="rtl"
      data-testid="tiktok-bridge-banner"
      style={{
        position: "fixed",
        top: 12, left: "50%", transform: "translateX(-50%)",
        zIndex: 900,
        maxWidth: 520,
        background: "rgba(24,10,10,0.92)",
        border: "1px solid rgba(180, 80, 80, 0.6)",
        color: "#fca5a5",
        padding: "6px 12px",
        fontFamily: "cairo, sans-serif",
        fontSize: 11,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        borderRadius: 6,
        boxShadow: "0 4px 15px rgba(0,0,0,0.5)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <AlertTriangle size={13} className="shrink-0 text-amber-400" />
        <span>{msg}</span>
      </div>
      <button
        onClick={() => setDismissed(true)}
        style={{
          background: "transparent",
          border: "none",
          color: "#fca5a5",
          cursor: "pointer",
          padding: 2,
          display: "flex", alignItems: "center",
        }}
        title="إغلاق التنبيه"
      >
        <X size={13} />
      </button>
    </div>,
    document.body
  );
}

export default TikTokEventBridge;
